import Papa from 'papaparse';
import { v4 as uuid } from 'uuid';

import { Table as CommonTable } from 'common/types/disco/table';

export type TableId = string;

export type Table = CommonTable & {
  compiled?: CommonTable;
};

export const tableFromFile = async (name: string, file: File): Promise<Table> => {
  const result: Papa.ParseResult<string[]> = await new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      header: false,
      skipEmptyLines: true,
      complete: result => resolve(result),
      error: err => reject(err),
    });
  });

  const columnNames = result.data[0] ?? [];

  // row length may differ from the number of columns, but this causes problems, so pad with ''
  const rows = (result.data.slice(1) ?? []).map(row => {
    if (row.length < columnNames.length)
      return row.concat(Array(columnNames.length - row.length).fill(''));
    if (row.length > columnNames.length) return row.slice(0, columnNames.length);
    return row;
  });

  return {
    name,
    id: uuid(),
    columnNames,
    rows,
  };
};

export type TableUpdate = {
  name?: string;
};

export const applyTableUpdate = (table: Table, update: TableUpdate) => {
  table.name = update.name ?? table.name;
};

export type Value = {
  // the currently selected mode
  selectedMode: 'column' | 'const';
  // the currently selected column. Ingnored if mode is not 'column'
  selectedColumnName?: string;
  // the currently entered value, ignored if mode is not 'const'
  value?: string;
};

/**
 * reads the value `v` from row `row` of table `t`
 * returns undefined if any of t, v, or row are not provided, or if the columnName selected
 * in v does not exist in the table
 */
export const getValue = (t?: Table, v?: Value, row?: number): string | undefined => {
  if (!t || !v || row === undefined) return undefined;
  if (v.selectedMode === 'const') {
    return v.value;
  }
  const columnIndex = t.columnNames.findIndex(n => n === v.selectedColumnName);
  if (columnIndex < 0) return undefined;
  return t.rows[row][columnIndex];
};

export type ValueUpdate = Partial<Value>;

export const applyValueUpdate = (value: Value, update?: ValueUpdate) => {
  if (!update) return;
  value.selectedMode = update.selectedMode ?? value.selectedMode;
  value.selectedColumnName = update.selectedColumnName ?? value.selectedColumnName;
  value.value = update.value ?? value.value;
};
